import { doc, getDoc, runTransaction, serverTimestamp } from 'firebase/firestore';
import { db } from './firebaseConfig.js';
import { RaceEntryRepository } from '../repositories/RaceEntryRepository.js';
import { RaceEntry } from '../../domain/models/RaceEntry.js';

export class FirebaseRaceEntryRepo extends RaceEntryRepository {
  async _mutate(resumeId, mutate) {
    const ref = doc(db, 'resumes', resumeId);
    await runTransaction(db, async transaction => {
      const snap = await transaction.get(ref);
      if (!snap.exists()) throw new Error('Resume no longer exists.');
      const races = mutate(snap.data().races ?? []);
      if (races.length > 200) throw new Error('A resume can contain up to 200 races.');
      transaction.update(ref, { races, updatedAt: serverTimestamp() });
    });
  }
  async add(resumeId, raceEntry) {
    const race = new RaceEntry({ ...raceEntry, id: raceEntry.id ?? crypto.randomUUID() });
    await this._mutate(resumeId, races => races.some(r => r.id === race.id) ? races : [...races, race.toFirestore()]);
    return race;
  }
  async addMany(resumeId, entries) {
    const additions = entries.map(entry => new RaceEntry({ ...entry, id: entry.id ?? crypto.randomUUID() }));
    await this._mutate(resumeId, races => [...races, ...additions.filter(r => !races.some(old => old.id === r.id)).map(r => r.toFirestore())]);
    return additions;
  }
  async update(resumeId, raceId, data) {
    await this._mutate(resumeId, races => {
      if (!races.some(r => r.id === raceId)) throw new Error('This race was deleted. Reopen the race list.');
      return races.map(r => r.id === raceId ? { ...r, ...data } : r);
    });
  }
  async delete(resumeId, raceId) {
    await this._mutate(resumeId, races => races.filter(r => r.id !== raceId));
  }
  async getByResumeId(resumeId) {
    const snap = await getDoc(doc(db, 'resumes', resumeId));
    if (!snap.exists()) throw new Error('Resume no longer exists.');
    return (snap.data().races ?? []).map(r => RaceEntry.fromFirestore(r));
  }
}
